import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningIcon from '@mui/icons-material/Warning';
import { truckService } from '../api/api';
import { confirmOdometerUpdate } from '../utils/odometer';
import ConfirmDialog from './ConfirmDialog';
import { formatPortalDate } from '../utils/dateFormat';

const today = () => new Date().toISOString().slice(0, 10);

function formatKm(value) {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = typeof value === 'number' ? value : parseInt(String(value).replace(/[^0-9-]/g, ''), 10);
  if (!Number.isFinite(parsed)) return String(value);
  return `${parsed.toLocaleString()} km`;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return formatPortalDate(date);
}

function formatMonths(value) {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = typeof value === 'number' ? value : parseInt(String(value).replace(/[^0-9-]/g, ''), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return '—';
  if (parsed % 12 === 0) return `${parsed / 12} year${parsed / 12 === 1 ? '' : 's'}`;
  return `${parsed} month${parsed === 1 ? '' : 's'}`;
}

function formatInterval(km, months) {
  const pieces = [];
  if (Number(km) > 0) pieces.push(formatKm(km));
  if (Number(months) > 0) pieces.push(formatMonths(months));
  return pieces.length ? pieces.join(' or ') : '—';
}

function monthsToYears(value) {
  const months = Number(value || 0);
  return months > 0 ? Number((months / 12).toFixed(2)) : '';
}

function yearsToMonths(value) {
  const years = parseFloat(String(value || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(years) && years > 0 ? Math.round(years * 12) : null;
}

function statusColor(status) {
  if (status === 'Amber') return { bg: '#fff8e1', color: '#f57c00', border: '#ffcc80' };
  if (status === 'Green') return { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' };
  return { bg: '#eceff1', color: '#546e7a', border: '#cfd8dc' };
}

function ConsumableStatusChip({ status }) {
  const colors = statusColor(status);
  return (
    <Chip
      label={status || 'Unknown'}
      size="small"
      sx={{
        bgcolor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        fontWeight: 900,
        minWidth: 82,
      }}
    />
  );
}

export default function TruckConsumables({ truckId, truck }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [rows, setRows] = useState([]);
  const [masterRows, setMasterRows] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [currentTruck, setCurrentTruck] = useState(truck || null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [selectedConsumable, setSelectedConsumable] = useState(null);
  const [replaceOdometer, setReplaceOdometer] = useState('');
  const [replaceDate, setReplaceDate] = useState(today());
  const [replaceNotes, setReplaceNotes] = useState('');
  const [savingReplacement, setSavingReplacement] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingEditorOpen, setSettingEditorOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [settingForm, setSettingForm] = useState({
    name: '',
    category: '',
    expected_service_life_km: '',
    expected_service_life_years: '',
    email_enabled: false,
    email_recipients: '',
    active: true,
  });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRow, setHistoryRow] = useState(null);
  const [deleteSetting, setDeleteSetting] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadConsumables = async () => {
    if (!truckId) return;
    try {
      setLoading(true);
      const [consumablesRes, masterRes] = await Promise.all([
        truckService.getConsumables(truckId),
        truckService.getConsumableMaster(),
      ]);
      const vehicleRows = consumablesRes.data?.rows || [];
      const masterRowsData = masterRes.data || [];
      const masterById = new Map(masterRowsData.map((row) => [Number(row.id), row]));
      const rowsWithMasterSettings = vehicleRows.map((row) => ({
        ...(masterById.get(Number(row.id)) || {}),
        ...row,
        vehicle_ids: masterById.get(Number(row.id))?.vehicle_ids || row.vehicle_ids || [],
      }));
      const hasVehicleAssignments = masterRowsData.some((row) => Array.isArray(row.vehicle_ids) && row.vehicle_ids.length > 0);
      const vehicleSettings = hasVehicleAssignments
        ? masterRowsData.filter((row) => (row.vehicle_ids || []).map(Number).includes(Number(truckId)))
        : masterRowsData;
      setRows(rowsWithMasterSettings);
      setReminders(consumablesRes.data?.reminders || []);
      setCurrentTruck(consumablesRes.data?.truck || truck || null);
      setMasterRows(vehicleSettings);
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to load scheduled maintenance reminders.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConsumables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckId]);

  useEffect(() => {
    setCurrentTruck(truck || null);
  }, [truck]);

  const categories = useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.category).filter(Boolean))).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesCategory = categoryFilter === 'all' || row.category === categoryFilter;
      const matchesSearch = !q || row.name?.toLowerCase().includes(q) || row.category?.toLowerCase().includes(q) || row.status?.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [categoryFilter, rows, search]);

  const statusCounts = useMemo(() => {
    return rows.reduce((acc, row) => {
      acc[row.status] = (acc[row.status] || 0) + 1;
      return acc;
    }, {});
  }, [rows]);

  const groupedRows = useMemo(() => {
    return filteredRows.reduce((acc, row) => {
      const category = row.category || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(row);
      return acc;
    }, {});
  }, [filteredRows]);

  const openReplace = (row) => {
    setSelectedConsumable(row);
    setReplaceOdometer(row.current_odometer ?? currentTruck?.odometer ?? '');
    setReplaceDate(today());
    setReplaceNotes('');
    setReplaceDialogOpen(true);
  };

  const handleReplacement = async () => {
    if (!truckId || !selectedConsumable) return;
    if (replaceOdometer === '' || replaceOdometer === null || replaceOdometer === undefined) {
      setSnackbar({ open: true, severity: 'error', message: 'Current odometer is required.' });
      return;
    }

    try {
      setSavingReplacement(true);
      if (!await confirmOdometerUpdate({
        currentValue: currentTruck?.odometer,
        nextValue: replaceOdometer,
        context: `record ${selectedConsumable.name} replacement and update the vehicle odometer`,
      })) {
        setSavingReplacement(false);
        return;
      }

      await truckService.replaceConsumable(truckId, selectedConsumable.id, {
        installed_at_odometer: replaceOdometer,
        service_date: replaceDate,
        notes: replaceNotes || null,
      });

      setReplaceDialogOpen(false);
      await loadConsumables();
      window.dispatchEvent(new Event('vehiclesUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      window.dispatchEvent(new Event('remindersUpdated'));
      setSnackbar({ open: true, severity: 'success', message: `${selectedConsumable.name} replacement recorded.` });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to record replacement.',
      });
    } finally {
      setSavingReplacement(false);
    }
  };

  const openEditSetting = (row = null) => {
    setSettingEditorOpen(true);
    setEditingSetting(row);
    setSettingForm(row ? {
      name: row.name || '',
      category: row.category || '',
      expected_service_life_km: row.expected_service_life_km ?? '',
      expected_service_life_years: monthsToYears(row.expected_service_life_months),
      email_enabled: row.email_enabled === true,
      email_recipients: row.email_recipients || '',
      active: row.active !== false,
    } : {
      name: '',
      category: '',
      expected_service_life_km: '',
      expected_service_life_years: '',
      email_enabled: false,
      email_recipients: '',
      active: true,
    });
  };

  const saveSetting = async () => {
    try {
      const reminderKm = Number(settingForm.expected_service_life_km || 0);
      const reminderMonths = yearsToMonths(settingForm.expected_service_life_years);
      if (reminderKm <= 0 && !reminderMonths) {
        setSnackbar({ open: true, severity: 'error', message: 'Set Reminder After by odometer km, years, or both.' });
        return;
      }
      const payload = {
        name: settingForm.name,
        category: settingForm.category,
        expected_service_life_km: reminderKm > 0 ? reminderKm : 0,
        expected_service_life_months: reminderMonths,
        email_enabled: settingForm.email_enabled,
        email_recipients: settingForm.email_enabled ? settingForm.email_recipients : '',
        active: settingForm.active,
      };
      if (!editingSetting?.id || (editingSetting?.vehicle_ids || []).length > 0) {
        payload.vehicle_ids = Array.from(new Set([...(editingSetting?.vehicle_ids || []), Number(truckId)].filter(Boolean).map(Number)));
      }
      if (editingSetting?.id) {
        await truckService.updateConsumableMaster(editingSetting.id, payload);
      } else {
        await truckService.createConsumableMaster(payload);
      }
      setEditingSetting(null);
      setSettingEditorOpen(false);
      await loadConsumables();
      window.dispatchEvent(new Event('remindersUpdated'));
      setSnackbar({ open: true, severity: 'success', message: 'Scheduled maintenance reminder saved.' });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to save scheduled maintenance reminder.',
      });
    }
  };

  const handleDeleteSetting = async () => {
    if (!deleteSetting?.id) return;
    try {
      await truckService.deleteConsumableMaster(deleteSetting.id);
      setDeleteSetting(null);
      await loadConsumables();
      window.dispatchEvent(new Event('remindersUpdated'));
      setSnackbar({ open: true, severity: 'success', message: 'Custom reminder deleted.' });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to delete reminder.',
      });
    }
  };

  const rowCard = (row) => {
    const colors = statusColor(row.status);
    return (
      <Paper key={row.id} sx={{ p: 2, borderRadius: 3, border: `1px solid ${colors.border}`, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>{row.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{row.category}</Typography>
          </Box>
          <ConsumableStatusChip status={row.status} />
        </Box>
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          <Grid item xs={6}><Typography variant="caption">Last Replaced</Typography><Typography sx={{ fontWeight: 800 }}>{formatKm(row.last_replaced_at_km)}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption">Current Age</Typography><Typography sx={{ fontWeight: 800 }}>{formatInterval(row.current_age_km, row.current_age_months)}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption">Reminder After</Typography><Typography sx={{ fontWeight: 800 }}>{formatInterval(row.expected_service_life_km, row.expected_service_life_months)}</Typography></Grid>
          <Grid item xs={6}>
            <Typography variant="caption">Due Point</Typography>
            <Typography sx={{ fontWeight: 800 }}>{formatInterval(row.next_due_odometer, null)}{row.next_due_date ? ` / ${formatDate(row.next_due_date)}` : ''}</Typography>
            {row.due_formula && <Typography variant="caption" color="text.secondary">{row.due_formula}</Typography>}
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Button size="small" variant="contained" startIcon={<BuildIcon />} onClick={() => openReplace(row)} sx={{ bgcolor: '#001f3f', fontWeight: 900 }}>Replace</Button>
          <Button size="small" variant="outlined" startIcon={<HistoryIcon />} onClick={() => { setHistoryRow(row); setHistoryOpen(true); }}>History</Button>
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, sm: 1.25 }, mb: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{
                width: { xs: 40, sm: 46 },
                height: { xs: 40, sm: 46 },
                borderRadius: 2,
                bgcolor: '#001f3f',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Inventory2Icon sx={{ fontSize: { xs: 23, sm: 27 } }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#001f3f', fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05, mb: 0.1 }}>
                  Scheduled Maintenance Reminders
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                  Current odometer: {formatKm(currentTruck?.odometer)}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => { window.location.hash = '/maintenance-reminders'; }}
              sx={{ fontWeight: 900, borderRadius: 1.6, px: 1.55, py: 0.62, minHeight: 36, fontSize: { xs: '0.78rem', sm: '0.86rem' } }}
            >
              Configure Reminders
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Green', value: statusCounts.Green || 0, color: '#2e7d32', bg: '#e8f5e9' },
              { label: 'Amber', value: statusCounts.Amber || 0, color: '#f57c00', bg: '#fff8e1' },
              { label: 'Unknown', value: statusCounts.Unknown || 0, color: '#546e7a', bg: '#eceff1' },
            ].map((item) => (
              <Grid item xs={6} md={3} key={item.label}>
                <Paper sx={{ p: 2, borderRadius: 3, bgcolor: item.bg, border: `1px solid ${item.color}22` }}>
                  <Typography variant="caption" sx={{ color: item.color, fontWeight: 900 }}>{item.label}</Typography>
                  <Typography variant="h4" sx={{ color: item.color, fontWeight: 900 }}>{item.value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {reminders.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid #ffcc80', bgcolor: '#fff8e1' }}>
              <Typography sx={{ fontWeight: 900, color: '#e65100', mb: 1 }}>
                Automated Reminders
              </Typography>
              <Grid container spacing={1.5}>
                {reminders.slice(0, 6).map((reminder) => (
                  <Grid item xs={12} md={6} key={reminder.consumable_id}>
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                      <Typography sx={{ fontWeight: 900 }}>{reminder.name}</Typography>
                      <Typography variant="body2">
                        {reminder.message}
                        {reminder.next_due_odometer ? ` Due at ${formatKm(reminder.next_due_odometer)}.` : ''}
                        {reminder.next_due_date ? ` Due by ${formatDate(reminder.next_due_date)}.` : ''}
                      </Typography>
                      {reminder.due_formula && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 800 }}>
                          Trigger: replacement odometer + reminder km = {reminder.due_formula}
                        </Typography>
                      )}
                    </Alert>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #e5eaf2' }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search reminder, category, or status"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                    <MenuItem value="all">All Categories</MenuItem>
                    {categories.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : filteredRows.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 3 }}>
              <WarningIcon sx={{ fontSize: 64, color: '#b0bec5', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">No scheduled maintenance reminders found</Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredRows.map(rowCard)}
            </Box>
          ) : (
            <TableContainer sx={{ border: '1px solid #d8e2ef', borderRadius: 2, overflowX: 'auto' }}>
              <Table stickyHeader sx={{ minWidth: 1050 }}>
                <TableHead>
                  <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#001f3f', color: 'white', fontWeight: 900 } }}>
                    <TableCell>Reminder</TableCell>
                    <TableCell align="right">Last Replaced At</TableCell>
                    <TableCell align="right">Current Age</TableCell>
                    <TableCell align="right">Reminder After</TableCell>
                    <TableCell align="right">Due Point</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(groupedRows).map(([category, categoryRows]) => (
                    <React.Fragment key={category}>
                      <TableRow>
                        <TableCell colSpan={7} sx={{ bgcolor: '#f7fafc', color: '#001f3f', fontWeight: 900 }}>
                          {category}
                        </TableCell>
                      </TableRow>
                      {categoryRows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Typography sx={{ fontWeight: 900 }}>{row.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Last service: {formatDate(row.last_service_date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{formatKm(row.last_replaced_at_km)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900 }}>{formatInterval(row.current_age_km, row.current_age_months)}</TableCell>
                          <TableCell align="right">{formatInterval(row.expected_service_life_km, row.expected_service_life_months)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatKm(row.next_due_odometer)}</Typography>
                            {row.due_formula && <Typography variant="caption" color="text.secondary">{row.due_formula}</Typography>}
                            {row.next_due_date && <Typography variant="caption" color="text.secondary">{formatDate(row.next_due_date)}</Typography>}
                          </TableCell>
                          <TableCell align="center"><ConsumableStatusChip status={row.status} /></TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Button size="small" variant="contained" startIcon={<BuildIcon />} onClick={() => openReplace(row)} sx={{ bgcolor: '#001f3f', fontWeight: 900 }}>Replace</Button>
                              <Tooltip title="History">
                                <IconButton size="small" onClick={() => { setHistoryRow(row); setHistoryOpen(true); }} sx={{ border: '1px solid #d8e2ef', borderRadius: 1 }}>
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={replaceDialogOpen} onClose={() => setReplaceDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          Record Replacement
          <IconButton onClick={() => setReplaceDialogOpen(false)} sx={{ position: 'absolute', right: 12, top: 10 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontWeight: 900, mb: 2 }}>{selectedConsumable?.name}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Current Odometer (from vehicle)" value={replaceOdometer} onChange={(e) => setReplaceOdometer(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Service Date" type="date" value={replaceDate} onChange={(e) => setReplaceDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Notes" value={replaceNotes} onChange={(e) => setReplaceNotes(e.target.value)} placeholder="Optional replacement notes" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReplaceDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={savingReplacement} onClick={handleReplacement} sx={{ bgcolor: '#001f3f', fontWeight: 900 }}>
            {savingReplacement ? 'Saving…' : 'Save Replacement'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={settingsOpen} onClose={() => { setSettingsOpen(false); setSettingEditorOpen(false); }} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          Scheduled Maintenance Reminder Settings
          <IconButton onClick={() => { setSettingsOpen(false); setSettingEditorOpen(false); }} sx={{ position: 'absolute', right: 12, top: 10 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => openEditSetting(null)} sx={{ fontWeight: 900 }}>
              Add Custom Reminder
            </Button>
          </Box>
          <TableContainer sx={{ border: '1px solid #d8e2ef', borderRadius: 2, maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#001f3f', color: 'white', fontWeight: 900 } }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Reminder After</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {masterRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 800 }}>{row.name}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell align="right">{formatInterval(row.expected_service_life_km, row.expected_service_life_months)}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" color={row.email_enabled ? 'info' : 'default'} label={row.email_enabled ? 'Email On' : 'Email Off'} sx={{ fontWeight: 800 }} />
                    </TableCell>
                    <TableCell align="center"><Chip size="small" label={row.active === false ? 'No' : 'Yes'} /></TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title="Edit reminder">
                          <IconButton size="small" onClick={() => openEditSetting(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete reminder">
                          <IconButton size="small" color="error" onClick={() => setDeleteSetting(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {settingEditorOpen && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#001f3f', mb: 2 }}>
                {editingSetting?.id ? 'Edit Reminder' : 'Add Custom Reminder'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Name" value={settingForm.name} onChange={(e) => setSettingForm((prev) => ({ ...prev, name: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Category" value={settingForm.category} onChange={(e) => setSettingForm((prev) => ({ ...prev, category: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth type="number" label="Reminder After (km)" value={settingForm.expected_service_life_km} onChange={(e) => setSettingForm((prev) => ({ ...prev, expected_service_life_km: e.target.value }))} inputProps={{ min: 0 }} helperText="Odometer based" />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth type="number" label="Reminder After (years)" value={settingForm.expected_service_life_years} onChange={(e) => setSettingForm((prev) => ({ ...prev, expected_service_life_years: e.target.value }))} inputProps={{ min: 0, step: 0.25 }} helperText="Time based" />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                    <Switch checked={settingForm.active} onChange={(e) => setSettingForm((prev) => ({ ...prev, active: e.target.checked }))} />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
                    <FormControlLabel
                      control={<Switch checked={settingForm.email_enabled} onChange={(e) => setSettingForm((prev) => ({ ...prev, email_enabled: e.target.checked }))} />}
                      label={<Typography sx={{ fontWeight: 900, color: '#001f3f' }}>Send Email When This Reminder Is Due</Typography>}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Email Recipients"
                      value={settingForm.email_recipients}
                      onChange={(e) => setSettingForm((prev) => ({ ...prev, email_recipients: e.target.value }))}
                      disabled={!settingForm.email_enabled}
                      helperText="Uses the same email recipient setting as the main Maintenance Reminders page."
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => { setEditingSetting(null); setSettingEditorOpen(false); }}>Cancel</Button>
                    <Button variant="contained" onClick={saveSetting} sx={{ bgcolor: '#001f3f', fontWeight: 900 }}>Save Setting</Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setSettingsOpen(false); setSettingEditorOpen(false); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          {historyRow?.name} History
          <IconButton onClick={() => setHistoryOpen(false)} sx={{ position: 'absolute', right: 12, top: 10 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!historyRow?.history?.length ? (
            <Typography color="text.secondary">No replacement history recorded yet.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900 }}>Service Date</TableCell>
                    <TableCell sx={{ fontWeight: 900 }} align="right">Installed At</TableCell>
                    <TableCell sx={{ fontWeight: 900 }} align="right">Removed At</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRow.history.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{formatDate(item.service_date)}</TableCell>
                      <TableCell align="right">{formatKm(item.installed_at_odometer)}</TableCell>
                      <TableCell align="right">{formatKm(item.removed_at_odometer)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{item.notes || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteSetting)}
        title="Delete Reminder?"
        description={`This will remove "${deleteSetting?.name || 'this reminder'}" from active scheduled maintenance reminders. Existing service history will stay saved.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDeleteSetting}
        onClose={() => setDeleteSetting(null)}
        icon={<DeleteIcon />}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
